import { z } from "zod";

import { FLIGHT_FARE_TYPE_VALUES } from "@/feature/flight/search/lib/passenger-types";
import { CHILD_AGE_LIMITS, FLIGHT_LIMITS } from "@/feature/flight/search/types/constants";

export const airportSchema = z.object({
  iata_code: z.string().min(1, "IATA code is required"),
  name: z.string().optional(),
  city_name: z.string().optional(),
  country: z.string().optional(),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
});

export const flightSearchSchema = z
  .object({
    tripType: z.enum(["oneway", "roundtrip", "multicity"]),
    fareType: z.enum(FLIGHT_FARE_TYPE_VALUES),
    passengers: z.object({
      adults: z
        .number()
        .min(FLIGHT_LIMITS.MIN_ADULTS, "At least 1 adult required")
        .max(FLIGHT_LIMITS.MAX_PASSENGERS),
      children: z.number().min(0).max(FLIGHT_LIMITS.MAX_PASSENGERS),
      infants: z.number().min(0).max(FLIGHT_LIMITS.MAX_PASSENGERS),
    }),
    preferredAirlines: z.array(z.string()).optional(),
    childrenAges: z.array(z.number().optional()),
    cabin: z.enum(["1", "2", "3", "4"]),
    flights: z
      .array(
        z.object({
          from: airportSchema,
          to: airportSchema,
          departDate: z.date().optional().nullable(),
        }),
      )
      .min(1, "At least one flight is required"),
  })
  .superRefine((data, ctx) => {
    data.flights.forEach((flight, index) => {
      if (!flight || typeof flight !== "object") {
        ctx.addIssue({
          code: "custom",
          message: "Flight data is required",
          path: ["flights", index],
        });
        return;
      }

      if (!flight.from || !flight.from.iata_code || flight.from.iata_code.trim() === "") {
        ctx.addIssue({
          code: "custom",
          message: "Origin is required",
          path: ["flights", index, "from"],
        });
      }

      if (!flight.to || !flight.to.iata_code || flight.to.iata_code.trim() === "") {
        ctx.addIssue({
          code: "custom",
          message: "Destination is required",
          path: ["flights", index, "to"],
        });
      } else if (
        flight.from &&
        flight.from.iata_code &&
        flight.from.iata_code === flight.to.iata_code
      ) {
        ctx.addIssue({
          code: "custom",
          message: "Same as destination",
          path: ["flights", index, "from"],
        });
        ctx.addIssue({
          code: "custom",
          message: "Same as origin",
          path: ["flights", index, "to"],
        });
      }

      if (!flight.departDate) {
        const isReturnFlight = data.tripType === "roundtrip" && index === 1;
        ctx.addIssue({
          code: "custom",
          message: isReturnFlight ? "Return date is required" : "Departure date is required",
          path: ["flights", index, "departDate"],
        });
      }
    });

    if (data.tripType === "oneway" && data.flights.length !== 1) {
      ctx.addIssue({
        code: "custom",
        message: "One way requires exactly 1 flight",
        path: ["flights"],
      });
    }

    if (data.tripType === "roundtrip" && data.flights.length !== 2) {
      ctx.addIssue({
        code: "custom",
        message: "Round trip requires exactly 2 flights",
        path: ["flights"],
      });
    }

    if (
      data.tripType === "roundtrip" &&
      data.flights.length === 2 &&
      data.flights[0]?.departDate &&
      data.flights[1]?.departDate &&
      data.flights[1].departDate < data.flights[0].departDate
    ) {
      ctx.addIssue({
        code: "custom",
        message: "Return date cannot be before departure date",
        path: ["flights", 1, "departDate"],
      });
    }

    if (data.tripType === "multicity") {
      if (data.flights.length < FLIGHT_LIMITS.MIN_MULTI_CITY_FLIGHTS) {
        ctx.addIssue({
          code: "custom",
          message: `Multi-city requires at least ${FLIGHT_LIMITS.MIN_MULTI_CITY_FLIGHTS} flight segments`,
          path: ["flights"],
        });
      }
      if (data.flights.length > FLIGHT_LIMITS.MAX_FLIGHTS) {
        ctx.addIssue({
          code: "custom",
          message: `Maximum ${FLIGHT_LIMITS.MAX_FLIGHTS} flight segments allowed`,
          path: ["flights"],
        });
      }

      let previousDepartDate: Date | null = null;

      data.flights.forEach((flight, index) => {
        if (!flight.departDate) {
          return;
        }

        if (previousDepartDate && flight.departDate < previousDepartDate) {
          ctx.addIssue({
            code: "custom",
            message: "This date cannot be before the previous flight",
            path: ["flights", index, "departDate"],
          });
        }

        previousDepartDate = flight.departDate;
      });
    }

    if (data.passengers.infants > data.passengers.adults) {
      ctx.addIssue({
        code: "custom",
        message: "Number of infants cannot exceed number of adults",
        path: ["passengers", "infants"],
      });
    }

    const total =
      data.passengers.adults + data.passengers.children + data.passengers.infants;
    if (total > FLIGHT_LIMITS.MAX_PASSENGERS) {
      ctx.addIssue({
        code: "custom",
        message: `Maximum ${FLIGHT_LIMITS.MAX_PASSENGERS} passengers allowed`,
        path: ["passengers"],
      });
    }

    if (data.passengers.children > 0) {
      const ages = data.childrenAges || [];

      if (ages.length !== data.passengers.children) {
        ctx.addIssue({
          code: "custom",
          message: "Please provide age for all children",
          path: ["childrenAges"],
        });
      }

      for (let index = 0; index < data.passengers.children; index++) {
        const age = ages[index];
        if (age === undefined || age === null) {
          ctx.addIssue({
            code: "custom",
            message: "Child age is required",
            path: ["childrenAges", index],
          });
        } else if (age < CHILD_AGE_LIMITS.MIN || age > CHILD_AGE_LIMITS.MAX) {
          ctx.addIssue({
            code: "custom",
            message: `Child age must be between ${CHILD_AGE_LIMITS.MIN} and ${CHILD_AGE_LIMITS.MAX} years`,
            path: ["childrenAges", index],
          });
        }
      }
    }
  });
